import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { serviceAreaEndpoints } from '@/lib/api/endpoints';
import { serviceAreaKeys } from '@/lib/queryKeys';

export interface UsRegionsCity {
  city: string;
  zipCount: number;
  /**
   * Sorted, deduped list of every ZIP covered by this city. The backend
   * surfaces it on every regions call so the picker can compute checkbox
   * state in-memory — no follow-up `/us/expand` round-trip needed.
   */
  zips: string[];
}

export interface UsRegionsCounty {
  county: string;
  zipCount: number;
  /**
   * Sorted, deduped list of every ZIP covered by this county. Includes
   * city-attributed AND city-less ZIPs in the same county, so ticking
   * the county-level checkbox is a true "everything in this county"
   * selection — wider than the union of its `cities[].zips`.
   */
  zips: string[];
  cities: UsRegionsCity[];
}

export interface UsRegionsState {
  code: string;
  name: string;
  zipCount: number;
  counties: UsRegionsCounty[];
}

export interface UsRegionsResponse {
  states: UsRegionsState[];
}

interface UseUsRegionsOptions {
  search?: string;
  state?: string;
  enabled?: boolean;
}

/**
 * Fetches the state / city tree powering the US region-picker (the
 * "Cities" alternative to the US polygon-draw flow).
 *
 * Backed by `GET /service-areas/us/regions`. The server always returns
 * all 51 states + DC (so the UI can show every card); the `cities`
 * arrays inside each state are filtered by `search` / `state` here on
 * the server so the FE never has to filter ~31 k rows itself.
 */
export function useUsRegions({ search, state, enabled = true }: UseUsRegionsOptions = {}) {
  const normalizedSearch = (search ?? '').trim().toLowerCase();
  const normalizedState = (state ?? '').trim().toUpperCase();

  return useApiQuery<UsRegionsResponse>({
    queryKey: [...serviceAreaKeys.all(), 'us-regions', normalizedSearch, normalizedState],
    queryFn: () => {
      const params = new URLSearchParams();
      if (normalizedSearch) params.set('search', normalizedSearch);
      if (normalizedState) params.set('state', normalizedState);
      const qs = params.toString();
      const url = qs
        ? `${serviceAreaEndpoints.usRegions()}?${qs}`
        : serviceAreaEndpoints.usRegions();
      return apiGet<UsRegionsResponse>(operationsClient, url);
    },
    enabled,
    // The dataset is static — once we've loaded it, it doesn't change
    // until the operations container restarts. Keep it fresh for the
    // session and drop the refocus refetch.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
